// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the face of an action chip. A task shows the assignee's photo with a
// ClickUp badge (initials when there is no photo); the other actions show
// their kind icon on a tonal disc.

import styles from '@app/features/konvo_assistant/components/action_chip/ActionChip.module.css';
import type {ActionChipController} from '@app/features/konvo_assistant/components/action_chip/state/ActionChipRun';
import type {ChoiceKey} from '@app/features/konvo_assistant/state/AssistantSuggestions';
import {initialsOf} from '@app/features/konvo_tasks/components/task_sheet/TaskFormat';
import {konvoPhotoFor} from '@app/features/konvo_tasks/utils/KonvoPeoplePhotos';
import {ClickUpLogo} from '@app/features/ui/components/icons/ClickUpLogo';
import {BellIcon, CalendarBlankIcon, CalendarPlusIcon, ClockIcon, EyeSlashIcon} from '@app/features/ui/icons/Icons';
import Users from '@app/features/user/state/Users';
import {getUserAvatarURL} from '@app/features/user/utils/AvatarUtils';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

export type ChipAvatarSize = 'row' | 'pill' | 'menu' | 'notice';

const ICON_SIZE: Record<ChipAvatarSize, number> = {row: 18, pill: 14, menu: 16, notice: 18};
const BADGE_SIZE: Record<ChipAvatarSize, number> = {row: 12, pill: 9, menu: 11, notice: 12};
const SIZE_CLASS: Record<ChipAvatarSize, string> = {
	row: styles.avatarRow,
	pill: styles.avatarPill,
	menu: styles.avatarMenu,
	notice: styles.avatarNotice,
};

export const ChipAvatar = observer(
	({chip, choice, size}: {chip: ActionChipController; choice: ChoiceKey; size: ChipAvatarSize}) => {
		const className = clsx(styles.avatar, SIZE_CLASS[size]);
		if (choice === 'task' || choice === 'task_me') {
			const assignee = chip.options[choice]?.assignee;
			const me = Users.currentUser;
			const photo =
				choice === 'task_me' && me?.avatar
					? getUserAvatarURL({id: me.id, avatar: me.avatar}, false, 64)
					: assignee
						? konvoPhotoFor({name: assignee.name, email: assignee.email})
						: null;
			const name = choice === 'task_me' ? (me?.globalName ?? me?.username ?? assignee?.name) : assignee?.name;
			return (
				<span className={className} aria-hidden="true">
					{photo ? (
						<img className={styles.photo} src={photo} alt="" />
					) : (
						<span className={styles.initials}>{initialsOf(name)}</span>
					)}
					<span className={styles.badge}>
						<ClickUpLogo size={BADGE_SIZE[size]} />
					</span>
				</span>
			);
		}
		const icon = ICON_SIZE[size];
		return (
			<span className={clsx(className, styles.kind)} aria-hidden="true">
				{choice === 'reminder' ? (
					<BellIcon size={icon} />
				) : choice === 'event' ? (
					<CalendarPlusIcon size={icon} />
				) : choice === 'block_time' ? (
					<ClockIcon size={icon} />
				) : choice === 'meeting' ? (
					<CalendarBlankIcon size={icon} />
				) : (
					<EyeSlashIcon size={icon} />
				)}
			</span>
		);
	},
);
